import { z } from 'zod';

/**
 * Bedarfsvorschau aus dem Projekt-Materialbedarf ableiten (ADR 0086).
 *
 * Der Bedarf ist im ERP längst gepflegt — als `ProjectMaterialItem` je Projekt.
 * Die Bedarfsvorschau tippte ihn bisher ein zweites Mal ab. Diese Ableitung
 * holt ihn stattdessen, und zwar mit zwei Festlegungen des Tech-Leads
 * (2026-08-10):
 *
 * 1. **Offener Bedarf**, nicht der volle: `required − reserviert − bestellt −
 *    eingegangen`. Was bestellt ist, hat der Lieferant schwarz auf weiß — es
 *    gehört nicht mehr in eine Vorschau.
 * 2. **Termin = Projekt-Liefertermin minus Vorlauf.** Materialpositionen tragen
 *    selbst kein Datum; der Vorlauf ist die Zeit, die das Material vor der
 *    Auslieferung im Haus sein muss.
 */
export const deriveForecastFromProjectsSchema = z.object({
  /**
   * Welche Projekte? Leer/weggelassen = alle Projekte mit offenem Bedarf.
   * Die Auswahl ist die einzige Stellschraube, mit der sich der Umfang einer
   * partner-sichtbaren Liste begrenzen lässt.
   */
  projectIds: z.array(z.string().uuid()).max(500).optional(),
  /**
   * Vorlauf in Wochen vor dem Projekt-Liefertermin. 0 = Termin = Liefertermin.
   * Deckel bei 104 Wochen — darüber ist es kein Vorlauf mehr, sondern ein
   * Tippfehler.
   */
  leadTimeWeeks: z.number().int().min(0).max(104).default(4),
});
export type DeriveForecastFromProjectsInput = z.infer<typeof deriveForecastFromProjectsSchema>;

/** Was die Ableitung getan hat — je Projekt eine Zeile für die Rückmeldung. */
export const forecastDeriveProjectSchema = z.object({
  projectId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  /** Erzeugte Vorschau-Zeilen aus diesem Projekt. */
  lineCount: z.number().int().nonnegative(),
});
export type ForecastDeriveProject = z.infer<typeof forecastDeriveProjectSchema>;

/**
 * Ergebnis der Ableitung. Bewusst mit den **übersprungenen** Zahlen: Eine
 * Ableitung, die stillschweigend die Hälfte weglässt, ist schlimmer als eine,
 * die gar nicht läuft.
 */
export const forecastDeriveResultSchema = z.object({
  /** Neu geschriebene abgeleitete Zeilen. */
  created: z.number().int().nonnegative(),
  /** Ersetzte (vorher abgeleitete) Zeilen derselben Projekte. */
  replaced: z.number().int().nonnegative(),
  /** Von Hand gepflegte Zeilen der Liste — unangetastet geblieben. */
  keptManual: z.number().int().nonnegative(),
  /** Positionen ohne Artikel-Verknüpfung: Ohne Artikel gibt es keinen Lieferanten. */
  skippedWithoutArticle: z.number().int().nonnegative(),
  /**
   * Positionen, die einem ANDEREN Lieferanten gehören. Nur bei einer Vorschau
   * mit Lieferant > 0 — sonst wird nicht gefiltert.
   */
  skippedOtherSupplier: z.number().int().nonnegative(),
  /** Positionen, deren Projekt keinen Liefertermin hat — übernommen ohne Termin. */
  withoutDate: z.number().int().nonnegative(),
  projects: z.array(forecastDeriveProjectSchema),
});
export type ForecastDeriveResult = z.infer<typeof forecastDeriveResultSchema>;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Termin einer abgeleiteten Zeile: **Projekt-Liefertermin minus Vorlauf**.
 *
 * Steht bewusst hier und nicht nur in der API: Der Ableiten-Dialog im ERP zeigt
 * dieselbe Rechnung als Vorschau an, bevor jemand den Knopf drückt. Zwei
 * Implementierungen derselben Regel driften — dann verspricht die Vorschau
 * einen anderen Termin, als hinterher in der Liste steht.
 *
 * Ohne Liefertermin bleibt die Zeile **terminlos** statt geraten zu werden: Ein
 * erfundener Termin wäre schlimmer als keiner, der Lieferant plant danach.
 */
export function expectedDateFromDelivery(
  deliveryDate: Date | string | null | undefined,
  leadTimeWeeks: number,
): Date | null {
  if (!deliveryDate) return null;
  const base = deliveryDate instanceof Date ? deliveryDate : new Date(deliveryDate);
  if (Number.isNaN(base.getTime())) return null;
  return new Date(base.getTime() - leadTimeWeeks * 7 * DAY_MS);
}

/** Ein Projekt zur Auswahl im Ableiten-Dialog (mit Umfang des offenen Bedarfs). */
export const forecastDeriveCandidateSchema = z.object({
  projectId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  customerName: z.string().nullable(),
  /** Liefertermin des Projekts (ISO) — Grundlage des Vorschau-Termins. */
  deliveryDate: z.string().datetime().nullable(),
  /** Positionen mit offenem Bedarf UND Artikel-Verknüpfung. */
  openItemCount: z.number().int().nonnegative(),
});
export type ForecastDeriveCandidate = z.infer<typeof forecastDeriveCandidateSchema>;
